import { useAuth0 } from "@auth0/auth0-react";
import { InfiniteData, UseInfiniteQueryResult, UseQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { customClient } from "@utils/helpers";
import { unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { PropsWithChildren, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  CalendarDateRange,
  SearchableDropdownItem,
  SelectItem,
  VideoPlayerHandle,
} from "@fronterahealth/frontera-ui-components";

import {
  ClassificationSubClassEnum,
  GetMediaByCaregiverByMediaIdQuery,
  GetMediaByCaregiverDocument,
  GetMediaByCaregiverQuery,
  GetMediaByProviderByMediaIdQuery,
  GetMediaByProviderDocument,
  GetMediaByProviderQuery,
  GetMediaCommentsByMediaIdQuery,
  GetMediaGoalMomentsQuery,
  GetSessionSummaryBySessionIdQuery,
  GetTranscriptDataByMediaIdDocument,
  MediaCommentFilterEnum,
  MediaSortByEnum,
  TranscriptLineRecordTypeEdge,
  VideoSessionStatusEnums,
  useGetMediaByCaregiverByMediaIdQuery,
  useGetMediaByProviderByMediaIdQuery,
  useGetMediaCommentsByMediaIdQuery,
  useGetMediaGoalMomentsQuery,
  useGetSessionSummaryBySessionIdQuery,
} from "@api/graphql/types-and-hooks";
import { Comment, transformCommentData } from "@pages/VideoSessionsV2/components/Comments/helper";
import { MediaCardType } from "@pages/VideoSessionsV2/components/MediaCards";
import { convertMediaCardInteractionsToVideoMoments } from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { SideMomentPanelItem } from "@pages/VideoSessionsV2/types";
import { useAdminData } from "@providers/AdminDataProvider";

export type videoSessionNavigationTabType = "activity" | "transcript" | "summary" | "moments";
type CombinedMediaQueryResult = InfiniteData<
  GetMediaByProviderQuery["getMediaByProvider"] | GetMediaByCaregiverQuery["getMediaByCaregiver"],
  unknown
>;
export interface VideoMomentType {
  id: string;
  startTime: number;
  endTime: number;
  confidenceLevel: number;
  type?: ClassificationSubClassEnum | null;
}

export type GoalMoment = NonNullable<
  NonNullable<NonNullable<GetMediaGoalMomentsQuery["getMediaGoalMoments"]>["edges"][number]>["node"]
>;
export type TargetMoment = NonNullable<NonNullable<GoalMoment["targets"]["edges"][number]>["node"]>;
export type GoalMomentMediaTargetPrediction = NonNullable<
  NonNullable<GoalMoment["mediatargetpredictionSet"]["edges"][number]>["node"]
>;

interface VideoProvidersContextI {
  selectedTab: videoSessionNavigationTabType;
  setSelectedTab: (val: videoSessionNavigationTabType) => void;
  selectedMedia: MediaCardType | null; // This will now be derived from URL
  setSelectedMedia: React.Dispatch<React.SetStateAction<MediaCardType | null>>;
  filterSelectedClients: SearchableDropdownItem[];
  setFilterSelectedClients: (val: SearchableDropdownItem[]) => void;
  filterSelectedProviders: SearchableDropdownItem[];
  setFilterSelectedProviders: (val: SearchableDropdownItem[]) => void;
  filterSelectedCaregivers: SearchableDropdownItem[];
  setFilterSelectedCaregivers: (val: SearchableDropdownItem[]) => void;
  filterSessionDates: CalendarDateRange | undefined;
  setFilterSessionDates: (val: CalendarDateRange) => void;
  filterSelectedRating: SelectItem | undefined;
  setFilterSelectedRating: (val: SelectItem | undefined) => void;
  playerRef: React.RefObject<VideoPlayerHandle | null>;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  sortBy: SelectItem;
  setSortBy: (val: SelectItem) => void;
  totalFilteredMediaCount: number;
  setTotalFilteredMediaCount: (val: number) => void;
  filteredCommentType: SearchableDropdownItem[] | [];
  setFilteredCommentType: (val: SearchableDropdownItem[]) => void;
  commentsByMediaIdQuery: UseQueryResult<GetMediaCommentsByMediaIdQuery, unknown>;
  mediaGoalMomentsQuery: UseQueryResult<GetMediaGoalMomentsQuery, unknown>;
  editingCommentId: string | null;
  setEditingCommentId: (val: string | null) => void;
  allTranscriptData: TranscriptLineRecordTypeEdge[] | [];
  isTranscriptLoading: boolean;
  clearFilters: () => void;
  clearFeedFilters: () => void;
  isVideoDetailsPage: boolean;
  getSessionSummaryBySessionIdQuery: UseQueryResult<GetSessionSummaryBySessionIdQuery, unknown>;

  isMediaLoading: boolean;
  setIsMediaLoading: (val: boolean) => void;
  allFilteredMedias: MediaCardType[];
  setAllFilteredMedias: React.Dispatch<React.SetStateAction<MediaCardType[]>>;
  allFilteredComments: Comment[];
  setAllFilteredComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  goalMoments: GoalMoment[];
  filterIsReviewed: boolean | undefined;
  setFilterIsReviewed: (val: boolean | undefined) => void;
  isFilterActive: boolean | undefined;
  setIsFilterActive: (val: boolean) => void;
  filterByUploadStatus: SearchableDropdownItem[];
  setFilterByUploadStatus: (val: SearchableDropdownItem[]) => void;

  sidebarMomentsList: SideMomentPanelItem[];
  setSidebarMomentsList: (val: SideMomentPanelItem[]) => void;

  filteredMomentType: SearchableDropdownItem[] | [];
  setFilteredMomentType: (val: SearchableDropdownItem[]) => void;

  getMediaQuery: UseInfiniteQueryResult<CombinedMediaQueryResult, Error>;

  deletedMediaIds: Set<string>;
  setDeletedMediaIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  viewedMediaMap: Record<string, boolean>;
  setViewedMediaMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  otherMaladaptiveBehaviorEnabled: boolean;

  getMediaByIdQuery:
    | UseQueryResult<GetMediaByProviderByMediaIdQuery, unknown>
    | UseQueryResult<GetMediaByCaregiverByMediaIdQuery, unknown>;

  expandAllGoals: boolean;
  setExpandAllGoals: React.Dispatch<React.SetStateAction<boolean>>;
  onlyMoments: boolean;
  setOnlyMoments: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGoalStatus: string | undefined;
  setSelectedGoalStatus: React.Dispatch<React.SetStateAction<string | undefined>>;

  activeMomentId: string | null;
  setActiveMomentId: React.Dispatch<React.SetStateAction<string | null>>;

  activeMomentGoalId: string | null;
  setActiveMomentGoalId: React.Dispatch<React.SetStateAction<string | null>>;

  activeMomentTargetId: string | null;
  setActiveMomentTargetId: React.Dispatch<React.SetStateAction<string | null>>;

  mediaClassificationsData: VideoMomentType[];
}

const VideoSessionContext = createContext<VideoProvidersContextI | undefined>(undefined);

export const useVideoSessionData = () => {
  const context = useContext(VideoSessionContext);
  if (context === undefined) {
    throw new Error("useVideoSessionData must be used within a VideoSessionProvider");
  }
  return context;
};
export const defaultSortBy = {
  id: MediaSortByEnum.DescSessionUploadDate,
  primary: "Newest Upload",
};
const momentsList: SideMomentPanelItem[] = [
  { label: "Aggression", isVisible: true, color: "globemallow-500" },
  { label: "Screaming & Crying", isVisible: true, color: "hatch-500" },
];
export const VideoSessionProviderV2: React.FC<PropsWithChildren> = ({ children }) => {
  const { mediaId } = useParams();
  const { pathname } = useLocation();

  const { isUserCaregiver } = useAdminData();
  const { getAccessTokenSilently } = useAuth0();
  const flags = useFlags<FeatureFlags>();
  const otherMaladaptiveBehaviorEnabled = flags["other-maladaptive-ui-presentation"];

  // Fetch media data based on URL parameter
  const getMediaByIdQuery = isUserCaregiver
    ? useGetMediaByCaregiverByMediaIdQuery(
        { mediaId: mediaId || "" },
        { enabled: Boolean(mediaId), refetchOnWindowFocus: false },
      )
    : useGetMediaByProviderByMediaIdQuery(
        { mediaId: mediaId || "" },
        { enabled: Boolean(mediaId), refetchOnWindowFocus: false },
      );

  const { data: mediaData } = getMediaByIdQuery;
  const [selectedMedia, setSelectedMedia] = useState<MediaCardType | null>(null);
  const [selectedTab, setSelectedTab] = useState<videoSessionNavigationTabType>("activity");
  const [filterSelectedClients, setFilterSelectedClients] = useState<SearchableDropdownItem[]>([]);
  const [filterSelectedProviders, setFilterSelectedProviders] = useState<SearchableDropdownItem[]>([]);
  const [filterSelectedCaregivers, setFilterSelectedCaregivers] = useState<SearchableDropdownItem[]>([]);

  const [filterSessionDates, setFilterSessionDates] = useState<CalendarDateRange | undefined>({});
  const [filterSelectedRating, setFilterSelectedRating] = useState<SelectItem | undefined>();
  const [filterIsReviewed, setFilterIsReviewed] = useState<boolean | undefined>();
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SelectItem>(defaultSortBy);
  const [filterByUploadStatus, setFilterByUploadStatus] = useState<SearchableDropdownItem[]>([]);

  const [totalFilteredMediaCount, setTotalFilteredMediaCount] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const playerRef = useRef<VideoPlayerHandle>(null);

  const [filteredCommentType, setFilteredCommentType] = useState<SearchableDropdownItem[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>("");
  const [allTranscriptData, setAllTranscriptData] = useState<TranscriptLineRecordTypeEdge[] | []>([]);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState<boolean>(false);
  const [sidebarMomentsList, setSidebarMomentsList] = useState<SideMomentPanelItem[]>(momentsList);

  const [filteredMomentType, setFilteredMomentType] = useState<SearchableDropdownItem[]>([]);
  const [deletedMediaIds, setDeletedMediaIds] = useState<Set<string>>(new Set());
  const [viewedMediaMap, setViewedMediaMap] = useState<Record<string, boolean>>({});

  const queryKey = React.useMemo(
    () => ["getMediaComments", mediaId, filteredCommentType],
    [mediaId, filteredCommentType],
  );

  const [isMediaLoading, setIsMediaLoading] = useState<boolean>(false);
  const [allFilteredMedias, setAllFilteredMedias] = useState<MediaCardType[]>([]);
  const [allFilteredComments, setAllFilteredComments] = useState<Comment[]>([]);
  const [goalMoments, setGoalMoments] = useState<GoalMoment[]>([]);

  const [expandAllGoals, setExpandAllGoals] = useState<boolean>(false);
  const [onlyMoments, setOnlyMoments] = useState<boolean>(false);
  const [selectedGoalStatus, setSelectedGoalStatus] = useState<string>();
  const [activeMomentId, setActiveMomentId] = useState<string | null>(null);

  const [activeMomentGoalId, setActiveMomentGoalId] = useState<string | null>(null);
  const [activeMomentTargetId, setActiveMomentTargetId] = useState<string | null>(null);

  const commentsByMediaIdQuery = useGetMediaCommentsByMediaIdQuery(
    {
      mediaId: mediaId || "",
      commentTypes: filteredCommentType.length
        ? (filteredCommentType.map((item) => item?.id) as unknown as MediaCommentFilterEnum)
        : undefined,
    },
    {
      queryKey,
      enabled: Boolean(mediaId) && pathname.includes(`/video-sessions-v2/${mediaId}`),
    },
  );

  const { data: commentsData } = commentsByMediaIdQuery;

  useEffect(() => {
    if (commentsData) {
      const comments: Comment[] = transformCommentData(commentsData);
      setAllFilteredComments(comments);
    }
  }, [commentsData]);

  const mediaGoalMomentsQuery = useGetMediaGoalMomentsQuery(
    {
      filter: {
        mediaId: mediaId || "",
        onlyMoments: onlyMoments,
        goalStatus: selectedGoalStatus || "",
      },
    },
    {
      queryKey: ["getMediaGoalMomentsQuery", mediaId, onlyMoments, selectedGoalStatus],
      enabled: Boolean(mediaId) && pathname.includes(`/video-sessions-v2/${mediaId}`),
    },
  );

  const { data: mediaGoalMomentsData } = mediaGoalMomentsQuery;

  useEffect(() => {
    if (mediaGoalMomentsData) {
      const goals = unwrapConnection(mediaGoalMomentsData.getMediaGoalMoments) || [];
      setGoalMoments(goals);
    }
  }, [mediaGoalMomentsData]);

  const transcriptByMediaIdQuery = useInfiniteQuery({
    queryKey: ["getTranscriptDataByMediaId", mediaId],
    queryFn: async ({ pageParam = { after: null } }) => {
      const { after } = pageParam;
      const variables = { mediaId, after };
      const token = await getAccessTokenSilently();
      const response = await customClient({ token, document: GetTranscriptDataByMediaIdDocument, variables });

      return response.getTranscriptDataByMediaId;
    },
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return {
          after: lastPage.pageInfo.endCursor, // Use the cursor for more efficient pagination
        };
      }
      return undefined; // No more pages to fetch
    },
    enabled: Boolean(mediaId),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const {
    data: transcriptData,
    isFetchingNextPage: isTranscriptNextPageFetching,
    isFetching: isTranscriptFetching,
    hasNextPage: isTranscriptHasNextPage,
    fetchNextPage: fetchTranscriptNextPage,
  } = transcriptByMediaIdQuery;

  // Transform mediaData into MediaCardType format

  useEffect(() => {
    if (!mediaData) {
      setSelectedMedia(null);
      return;
    }

    let media;

    if (isUserCaregiver && "getMediaByCaregiverByMediaId" in mediaData) {
      media = mediaData.getMediaByCaregiverByMediaId;
    } else if ("getMediaByProviderByMediaId" in mediaData) {
      media = mediaData.getMediaByProviderByMediaId;
    } else {
      setSelectedMedia(null);
      return;
    }

    if (!media) {
      setSelectedMedia(null);
      return;
    }

    setSelectedMedia({
      id: media.id,
      createdDate: media.createdDate,
      mediaType: media.mediaType,
      mediaStatus: media.mediaStatus,
      videoSession: media.videoSession,
      commentsCount: media.commentsCount,
      mediaClassifications: media.mediaClassifications,
      signedUploadUrl: media.signedUploadUrl,
      isViewed: media.isViewed,
      totalDuration: media.totalDuration,
      thumbnailManifest: media.thumbnailManifest,
    } as MediaCardType);
  }, [mediaData, isUserCaregiver]);

  const getSessionSummaryBySessionIdQuery = useGetSessionSummaryBySessionIdQuery(
    {
      videoSessionId: selectedMedia?.videoSession?.id || "",
    },
    {
      queryKey: ["getSessionSummaryBySessionId", selectedMedia],
      enabled: Boolean(selectedMedia?.videoSession?.id),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const fetchMedia = async ({ pageParam = { after: null } }) => {
    const { after } = pageParam;

    const variables = {
      after,
      sortBy: (sortBy.id as unknown as MediaSortByEnum) || MediaSortByEnum.DescSessionUploadDate,
      learnerIds: filterSelectedClients.map((item) => item.id),
      providerIds: !isUserCaregiver ? filterSelectedProviders.map((item) => item.id) : undefined,
      caregiverIds: !isUserCaregiver ? filterSelectedCaregivers.map((item) => item.id) : undefined,
      sessionStartDate: filterSessionDates?.startDate ? format(filterSessionDates.startDate, "yyyy-MM-dd") : undefined,
      sessionEndDate: filterSessionDates?.endDate ? format(filterSessionDates.endDate, "yyyy-MM-dd") : undefined,
      sessionRating: !isUserCaregiver ? parseInt(filterSelectedRating?.id || "") : undefined,
      isReviewed: filterIsReviewed,
      sessionStatus: finalUploadStatus.length ? (finalUploadStatus as unknown as VideoSessionStatusEnums[]) : undefined,
    };

    const token = await getAccessTokenSilently();

    const response = await customClient({
      token,
      document: isUserCaregiver ? GetMediaByCaregiverDocument : GetMediaByProviderDocument,
      variables,
    });

    return isUserCaregiver ? response.getMediaByCaregiver : response.getMediaByProvider;
  };

  const finalUploadStatus = filterByUploadStatus.flatMap((item) => {
    try {
      const parsed = JSON.parse(item.id);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [item.id];
    }
  });

  const getMediaQuery = useInfiniteQuery({
    queryKey: [
      isUserCaregiver ? "getMediaByCaregiver" : "getMediaByProvider",
      filterSelectedClients,
      !isUserCaregiver ? filterSelectedCaregivers : null,
      !isUserCaregiver ? filterSelectedProviders : null,
      filterSessionDates?.startDate,
      filterSessionDates?.endDate,
      !isUserCaregiver ? filterSelectedRating : null,
      filterIsReviewed,
      finalUploadStatus,
      sortBy,
    ],
    queryFn: fetchMedia,
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? { after: lastPage.pageInfo.endCursor } : undefined;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    const filteredList = otherMaladaptiveBehaviorEnabled
      ? momentsList
      : momentsList.filter((item) => item.label !== "Aggression");

    setSidebarMomentsList(filteredList);
  }, [otherMaladaptiveBehaviorEnabled, momentsList]);

  useEffect(() => {
    setIsFilterActive(
      filterSelectedClients.length > 0 ||
        filterSelectedProviders.length > 0 ||
        filterSelectedCaregivers.length > 0 ||
        filterByUploadStatus.length > 0 ||
        (filterSessionDates?.startDate !== undefined && filterSessionDates?.endDate !== undefined) ||
        filterSelectedRating !== undefined,
    );
  }, [
    filterSelectedClients,
    filterSelectedCaregivers,
    filterSelectedProviders,
    filterSessionDates,
    filterSelectedRating,
    filterByUploadStatus,
  ]);

  useEffect(() => {
    if (isTranscriptFetching || isTranscriptNextPageFetching) {
      setIsTranscriptLoading(true);
    } else {
      setIsTranscriptLoading(false);
    }
  }, [isTranscriptFetching, isTranscriptNextPageFetching]);

  useEffect(() => {
    if (isTranscriptHasNextPage && !isTranscriptNextPageFetching) {
      fetchTranscriptNextPage();
    }
  }, [isTranscriptHasNextPage, isTranscriptNextPageFetching, fetchTranscriptNextPage]);

  useEffect(() => {
    if (transcriptData && !isTranscriptHasNextPage && !isTranscriptNextPageFetching) {
      const dataTemp = transcriptData.pages
        .map((group) => {
          return [...group.edges];
        })
        .flat(); // Append new edges
      setAllTranscriptData(dataTemp);
    }
  }, [transcriptData, isTranscriptHasNextPage, isTranscriptNextPageFetching]);

  const mediaClassificationsData: VideoMomentType[] = useMemo(
    () =>
      convertMediaCardInteractionsToVideoMoments(
        selectedMedia || ({} as MediaCardType),
        otherMaladaptiveBehaviorEnabled,
      ),
    [selectedMedia, otherMaladaptiveBehaviorEnabled],
  );
  const clearFilters = () => {
    setSelectedTab("activity");
    setFilteredCommentType([]);
  };

  const clearFeedFilters = () => {
    setSortBy(defaultSortBy);
    setFilterSelectedClients([]);
    setFilterSelectedProviders([]);
    setFilterSessionDates({});
    setFilterByUploadStatus([]);
    setFilterSelectedRating(undefined);
    setIsFilterActive(false);
    setFilterSelectedCaregivers([]);
  };

  return (
    <VideoSessionContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        selectedMedia,
        setSelectedMedia,
        filterSelectedClients,
        setFilterSelectedClients,
        filterSelectedProviders,
        setFilterSelectedProviders,
        filterSelectedCaregivers,
        setFilterSelectedCaregivers,
        filterSessionDates,
        setFilterSessionDates,
        filterSelectedRating,
        setFilterSelectedRating,
        sortBy,
        setSortBy,
        totalFilteredMediaCount,
        setTotalFilteredMediaCount,
        playerRef,
        currentTime,
        setCurrentTime,
        filteredCommentType,
        setFilteredCommentType,
        commentsByMediaIdQuery,
        mediaGoalMomentsQuery,
        editingCommentId,
        setEditingCommentId,

        allTranscriptData,
        isTranscriptLoading,
        clearFilters,
        isVideoDetailsPage: !!mediaId,
        getSessionSummaryBySessionIdQuery,
        isMediaLoading,
        setIsMediaLoading,
        allFilteredMedias,
        setAllFilteredMedias,
        allFilteredComments,
        setAllFilteredComments,
        goalMoments,
        filterIsReviewed,
        setFilterIsReviewed,
        clearFeedFilters,
        isFilterActive,
        setIsFilterActive,
        filterByUploadStatus,
        setFilterByUploadStatus,

        sidebarMomentsList,
        setSidebarMomentsList,

        filteredMomentType,
        setFilteredMomentType,

        getMediaQuery,
        deletedMediaIds,
        setDeletedMediaIds,
        viewedMediaMap,
        setViewedMediaMap,
        otherMaladaptiveBehaviorEnabled,
        getMediaByIdQuery,

        expandAllGoals,
        setExpandAllGoals,
        onlyMoments,
        setOnlyMoments,
        selectedGoalStatus,
        setSelectedGoalStatus,

        activeMomentId,
        setActiveMomentId,

        activeMomentGoalId,
        setActiveMomentGoalId,

        activeMomentTargetId,
        setActiveMomentTargetId,

        mediaClassificationsData,
      }}
    >
      {children}
    </VideoSessionContext.Provider>
  );
};
